import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Eye, EyeOff, ArrowLeft, Loader, CheckCircle } from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

const wakeBackend = () => {
  fetch(`${API_BASE}/api/health`).catch(() => {});
};

export default function AuthPage() {
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverWaking, setServerWaking] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const resetToken = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    wakeBackend();
    if (resetToken) setMode('reset');
    const savedTheme = localStorage.getItem('theme') || 'light';
    if (savedTheme === 'dark') document.documentElement.classList.add('dark');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const wakeTimer = setTimeout(() => setServerWaking(true), 3000);
    try {
      if (mode === 'login') {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.requiresVerification) {
            window.location.href = `/verify-email?email=${encodeURIComponent(data.email)}`;
            return;
          }
          throw new Error(data.message || 'Login failed');
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = '/dashboard';
      } else if (mode === 'signup') {
        const res = await fetch(`${API_BASE}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, email, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Signup failed');
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = `/verify-email?email=${encodeURIComponent(email)}`;
      } else if (mode === 'forgot') {
        const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Request failed');
        setMode('forgot-sent');
      } else if (mode === 'reset') {
        if (newPassword.length < 6) throw new Error('Password must be at least 6 characters');
        const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resetToken, newPassword }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Reset failed');
        setMode('reset-done');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      clearTimeout(wakeTimer);
      setServerWaking(false);
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setServerWaking(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4 transition-colors duration-300">
      <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 -right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
          <div className="p-8">

            {/* Logo */}
            <div className="text-center mb-6">
              <button onClick={() => window.location.href = '/'} className="inline-block hover:opacity-80 transition-opacity mb-4">
                <img src="/logos/logo-color.png" alt="HIVE" className="h-12 w-auto mx-auto dark:hidden" />
                <img src="/logos/logo-white.png" alt="HIVE" className="h-12 w-auto mx-auto hidden dark:block" />
              </button>
              <h1 className="text-2xl font-black bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                {mode === 'forgot' ? 'Forgot Password' :
                 mode === 'forgot-sent' ? 'Check Your Email' :
                 mode === 'reset' ? 'Set New Password' :
                 mode === 'reset-done' ? 'Password Reset!' :
                 'Welcome to HIVE'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {mode === 'login' ? 'Sign in to your account' :
                 mode === 'signup' ? 'Create your free account' :
                 mode === 'forgot' ? 'Enter your email to get a reset link' :
                 mode === 'forgot-sent' ? `We sent a link to ${email}` :
                 mode === 'reset' ? 'Choose a strong new password' :
                 'You can now sign in with your new password'}
              </p>
            </div>

            {/* Login / Signup toggle */}
            {(mode === 'login' || mode === 'signup') && (
              <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-2xl p-1 mb-6">
                <button onClick={() => switchMode('login')} className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${mode === 'login' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}>
                  Sign In
                </button>
                <button onClick={() => switchMode('signup')} className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${mode === 'signup' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg' : 'text-gray-600 dark:text-gray-400'}`}>
                  Sign Up
                </button>
              </div>
            )}

            {/* Back button */}
            {mode === 'forgot' && (
              <button onClick={() => switchMode('login')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Back to Sign In
              </button>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Server waking */}
            {serverWaking && (
              <div className="mb-4 px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl text-yellow-700 dark:text-yellow-400 text-sm flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin flex-shrink-0" />
                Server is waking up — this can take 20–30 seconds on the first try. Please wait…
              </div>
            )}

            {/* Forgot sent */}
            {mode === 'forgot-sent' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                  If <strong>{email}</strong> is registered, you'll receive a password reset link shortly. Check your spam folder too.
                </p>
                <button onClick={() => switchMode('login')} className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
                  Back to Sign In
                </button>
              </div>
            )}

            {/* Reset done */}
            {mode === 'reset-done' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">Your password has been reset successfully.</p>
                <button onClick={() => window.location.href = '/auth'} className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
                  Sign In Now
                </button>
              </div>
            )}

            {/* Form */}
            {(mode === 'login' || mode === 'signup' || mode === 'forgot' || mode === 'reset') && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'signup' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" required placeholder="John Doe" value={name} onChange={e => setName(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all" />
                    </div>
                  </div>
                )}
                {(mode === 'login' || mode === 'signup' || mode === 'forgot') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" required placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all" />
                    </div>
                  </div>
                )}
                {(mode === 'login' || mode === 'signup') && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Password</label>
                      {mode === 'login' && (
                        <button type="button" onClick={() => switchMode('forgot')} className="text-xs font-semibold text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 transition-colors">
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type={showPassword ? 'text' : 'password'} required placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-3 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                        {showPassword ? <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" /> : <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />}
                      </button>
                    </div>
                  </div>
                )}
                {mode === 'reset' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type={showPassword ? 'text' : 'password'} required placeholder="Min. 6 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-3 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                        {showPassword ? <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-600" /> : <Eye className="w-4 h-4 text-gray-400 hover:text-gray-600" />}
                      </button>
                    </div>
                  </div>
                )}
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold text-sm rounded-xl hover:shadow-xl hover:shadow-yellow-400/30 transition-all transform hover:scale-[1.02] disabled:opacity-60 disabled:scale-100 flex items-center justify-center gap-2">
                  {loading && <Loader className="w-4 h-4 animate-spin" />}
                  {loading ? 'Please wait…' :
                   mode === 'login' ? 'Sign In' :
                   mode === 'signup' ? 'Create Account' :
                   mode === 'forgot' ? 'Send Reset Link' :
                   'Set New Password'}
                </button>
              </form>
            )}

            {/* Terms for signup */}
            {mode === 'signup' && (
              <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
                By signing up you agree to our{' '}
                <a href="/terms" className="text-yellow-600 hover:text-yellow-700 font-semibold">Terms</a> and{' '}
                <a href="/privacy" className="text-yellow-600 hover:text-yellow-700 font-semibold">Privacy Policy</a>
              </p>
            )}
          </div>
        </div>

        {/* Bottom switch */}
        {(mode === 'login' || mode === 'signup') && (
          <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')} className="font-bold text-yellow-500 hover:text-yellow-600 transition-colors">
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}